/**
 * Every route to Premium that is not a full-price purchase, in one table.
 *
 * Three mechanisms, three data shapes, one question — "who got access, how, and
 * are they still using it":
 *
 *   promo-code  `PromoCodes` documents, with redemptions denormalised onto them
 *   granted     RevenueCat promotional entitlements (`rc_promo_*`), which
 *               includes the FriFlyt arrangement and anything granted by hand
 *   partner     Skida's own `users.partnerProgram` array
 *
 * ## Where each field comes from, and why
 *
 * Grant *type* is read from `users.activeEntitlements.Premium.productIdentifier`
 * rather than from the RevenueCat copy: the v2 API returns `product_id: null`
 * for promotional entitlements, so it cannot tell `rc_promo_Premium_custom`
 * from `rc_promo_Premium_yearly`.
 *
 * Whether access is *live* is computed from the expiry on the same document.
 * For grants that is sound — they do not auto-renew, so there is no renewal for
 * the mirror to miss. (That is exactly the staleness that makes the mirror
 * unusable for paid subscriptions, where RevenueCat is used instead.)
 */
import { collection, documentId, getDocs, query, where } from 'firebase/firestore'
import { productDb } from '@/shared/firebase/client'
import { PRODUCT_COLLECTIONS } from '@/shared/firebase/collections'
import { toIsoOrNull } from '@/shared/util/dates'

const IN_CHUNK = 30
const PREMIUM_PRODUCT = 'activeEntitlements.Premium.productIdentifier'

/** Promotional products RevenueCat grants. Extend as new ones are created. */
const GRANT_PRODUCTS: { id: string; label: string; note: string }[] = [
  { id: 'rc_promo_Premium_custom', label: 'Granted — custom', note: 'FriFlyt and other bespoke grants' },
  { id: 'rc_promo_Premium_yearly', label: 'Granted — 1 year', note: 'Manually granted, one year' },
  { id: 'rc_promo_Premium_lifetime', label: 'Granted — lifetime', note: 'Manually granted, no expiry' },
]

export type GrantKind = 'promo-code' | 'granted' | 'partner'

export interface GrantPerson {
  userId: string
  name: string
  email: string
  /** When they got access, ISO, when known. */
  at: string | null
  /** Whether their Premium entitlement is unexpired. */
  live: boolean
  periodType: string
  exists: boolean
}

export interface AccessGrantRow {
  kind: GrantKind
  id: string
  label: string
  detail: string
  /** People who received access this way. */
  total: number
  /** Of those, still holding access. */
  live: number
  lastAt: string | null
  /** Loaded with the row so the dialog needs no second fetch. */
  people: GrantPerson[]
  /** Promo codes only. */
  active?: boolean
  limit?: number
  /** Promo codes only: the document as stored, for the dialog's header. */
  promo?: PromoCodeInfo
}

/** `PromoCodes/{code}` — the fields skida-backend writes and reads. */
export interface PromoCodeInfo {
  code: string
  partner: string
  offering: string
  active: boolean
  /** 0 is unlimited. */
  limit: number
  redeemCount: number
  /** Access length after redeeming, in days. */
  durationDays: number | null
  /** The code's own end date, ISO. */
  expiresAt: string | null
  accessType: string
  createdAt: string | null
  updatedAt: string | null
  createdBy: string
  updatedBy: string
}

function promoInfo(id: string, d: Record<string, unknown>): PromoCodeInfo {
  const str = (v: unknown) => (typeof v === 'string' ? v : '')
  return {
    code: str(d.code) || id,
    partner: str(d.partner),
    offering: str(d.offering),
    active: d.active !== false,
    limit: typeof d.limit === 'number' ? d.limit : 0,
    redeemCount: typeof d.redeemCount === 'number' ? d.redeemCount : 0,
    durationDays: typeof d.durationDays === 'number' ? d.durationDays : null,
    expiresAt: toIsoOrNull(d.expiresAt),
    accessType: str(d.accessType),
    createdAt: toIsoOrNull(d.createdAt),
    updatedAt: toIsoOrNull(d.updatedAt),
    createdBy: str(d.createdBy),
    updatedBy: str(d.updatedBy),
  }
}

function premiumOf(d: Record<string, unknown>) {
  const ent = (d.activeEntitlements as Record<string, unknown> | undefined)?.Premium as
    | Record<string, unknown>
    | undefined
  return ent
}

function isLive(ent: Record<string, unknown> | undefined): boolean {
  const expires = ent?.expirationDateMillis
  // A lifetime grant has no expiry — absence means "no end date", not "expired".
  if (expires === null || expires === undefined) return true
  return typeof expires === 'number' && expires > Date.now()
}

function personFrom(id: string, d: Record<string, unknown> | undefined, at: string | null): GrantPerson {
  if (!d) return { userId: id, name: '', email: '', at, live: false, periodType: '', exists: false }
  const ent = premiumOf(d)
  return {
    userId: id,
    name: (d.displayName as string) ?? (d.name as string) ?? '',
    email: (d.email as string) ?? '',
    at,
    live: isLive(ent),
    periodType: (ent?.periodType as string) ?? '',
    exists: true,
  }
}

/** Users holding a given promotional product. */
async function loadGranted(productId: string): Promise<GrantPerson[]> {
  const snap = await getDocs(
    query(collection(productDb, PRODUCT_COLLECTIONS.users), where(PREMIUM_PRODUCT, '==', productId)),
  )
  return snap.docs.map((doc) => {
    const d = doc.data() as Record<string, unknown>
    const ent = premiumOf(d)
    return personFrom(doc.id, d, toIsoOrNull(ent?.originalPurchaseDateMillis ?? ent?.latestPurchaseDateMillis))
  })
}

/** Users carrying a partner-program entry, grouped by partner name. */
async function loadPartners(): Promise<Map<string, GrantPerson[]>> {
  const snap = await getDocs(
    query(collection(productDb, PRODUCT_COLLECTIONS.users), where('partnerProgram', '!=', null)),
  )
  const out = new Map<string, GrantPerson[]>()
  for (const doc of snap.docs) {
    const d = doc.data() as Record<string, unknown>
    const entries = Array.isArray(d.partnerProgram) ? d.partnerProgram : []
    for (const entry of entries as { partner?: string }[]) {
      const name = entry?.partner ?? '(unnamed)'
      const list = out.get(name) ?? []
      list.push(personFrom(doc.id, d, null))
      out.set(name, list)
    }
  }
  return out
}

/** Promo-code redeemers, resolved to people. */
async function loadRedeemers(stamps: Map<string, string | null>): Promise<GrantPerson[]> {
  const ids = [...stamps.keys()]
  if (ids.length === 0) return []
  const found = new Map<string, Record<string, unknown>>()
  for (let i = 0; i < ids.length; i += IN_CHUNK) {
    const chunk = ids.slice(i, i + IN_CHUNK)
    const snap = await getDocs(
      query(collection(productDb, PRODUCT_COLLECTIONS.users), where(documentId(), 'in', chunk)),
    )
    for (const doc of snap.docs) found.set(doc.id, doc.data() as Record<string, unknown>)
  }
  return ids
    .map((id) => personFrom(id, found.get(id), stamps.get(id) ?? null))
    .sort((a, b) => (b.at ?? '').localeCompare(a.at ?? ''))
}

function redemptionStamps(raw: unknown): Map<string, string | null> {
  const out = new Map<string, string | null>()
  if (!Array.isArray(raw)) return out
  for (const entry of raw as { userId?: unknown; timestamp?: unknown }[]) {
    const id = typeof entry.userId === 'string' ? entry.userId : ''
    if (!id) continue
    const at = toIsoOrNull(entry.timestamp)
    const prev = out.get(id)
    const earlier = at !== null && (prev === null || (prev !== undefined && at < prev))
    if (prev === undefined || earlier) out.set(id, at)
  }
  return out
}

export async function loadAccessGrants(): Promise<AccessGrantRow[]> {
  const [codesSnap, partners, ...grantLists] = await Promise.all([
    getDocs(collection(productDb, PRODUCT_COLLECTIONS.promoCodes)),
    loadPartners(),
    ...GRANT_PRODUCTS.map((p) => loadGranted(p.id)),
  ])

  const rows: AccessGrantRow[] = []

  // --- promo codes ---
  const codeRows = await Promise.all(
    codesSnap.docs.map(async (doc) => {
      const d = doc.data()
      const stamps = redemptionStamps(d.redeemed)
      const people = await loadRedeemers(stamps)
      const all = [...stamps.values()].filter((v): v is string => Boolean(v)).sort()
      return {
        kind: 'promo-code' as const,
        id: d.code ?? doc.id,
        label: d.code ?? doc.id,
        detail: d.offering ?? '',
        total:
          typeof d.redeemCount === 'number'
            ? d.redeemCount
            : Array.isArray(d.redeemed)
              ? d.redeemed.length
              : 0,
        live: people.filter((p) => p.live).length,
        lastAt: all[all.length - 1] ?? null,
        people,
        active: d.active !== false,
        limit: typeof d.limit === 'number' ? d.limit : 0,
        promo: promoInfo(doc.id, d),
      }
    }),
  )
  rows.push(...codeRows)

  // --- granted entitlements ---
  GRANT_PRODUCTS.forEach((p, i) => {
    const people = grantLists[i] ?? []
    if (people.length === 0) return
    const stamps = people.map((x) => x.at).filter((v): v is string => Boolean(v)).sort()
    rows.push({
      kind: 'granted',
      id: p.id,
      label: p.label,
      detail: p.note,
      total: people.length,
      live: people.filter((x) => x.live).length,
      lastAt: stamps[stamps.length - 1] ?? null,
      people,
    })
  })

  // --- partner programmes ---
  for (const [name, people] of partners) {
    rows.push({
      kind: 'partner',
      id: `partner:${name}`,
      label: name,
      detail: 'Partner programme',
      total: people.length,
      live: people.filter((x) => x.live).length,
      lastAt: null,
      people,
    })
  }

  // Most-used first, but keep the kinds grouped so the table reads as three
  // sections rather than an interleaved list.
  const order: Record<GrantKind, number> = { 'promo-code': 0, granted: 1, partner: 2 }
  return rows.sort(
    (a, b) => order[a.kind] - order[b.kind] || b.total - a.total || a.label.localeCompare(b.label),
  )
}
